import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  BrokeragePostInput,
  BrokeragePostResult,
  BrokerageProposal,
  BrokerageProposalInput,
  CompleteReconciliationInput,
  CorrectReconciliationInput,
  CreateReconciliationInput,
  HashChainVerifyResult,
  LinkFiledStatementsInput,
  LinkFiledStatementsResult,
  ParseStatementInput,
  ParsedStatement,
  ReconcilableItem,
  Reconciliation,
  ReconciliationStatus,
  ReconciliationWorksheet,
  SetReconciliationItemsInput,
  StartReconFromStatementInput,
  StatementPreviewInput,
  StatementReconPreview,
  UpdateReconciliationInput,
} from '@accounting/shared';
import { api } from '../lib/api';

export type ReconciliationWithStatus = Reconciliation & {
  effectiveStatus: ReconciliationStatus;
  voidedByReconciliationId?: string | null;
};

// ── Keys ────────────────────────────────────────────────────────────────────

export const reconciliationsKey = (
  companyId: string | null,
  bankAccountId?: string,
) =>
  ['reconciliations', companyId ?? 'none', bankAccountId ?? 'all'] as const;

export const reconciliationKey = (companyId: string | null, id: string) =>
  ['reconciliation', companyId ?? 'none', id] as const;

export const reconcilableItemsKey = (companyId: string | null, id: string) =>
  ['reconciliation-items', companyId ?? 'none', id] as const;

// ── Queries ─────────────────────────────────────────────────────────────────

export function useReconciliations(
  companyId: string | null,
  bankAccountId?: string,
) {
  return useQuery({
    queryKey: reconciliationsKey(companyId, bankAccountId),
    enabled: companyId !== null,
    queryFn: () => {
      const qs = bankAccountId ? `?bankAccountId=${bankAccountId}` : '';
      return api
        .get<{ reconciliations: ReconciliationWithStatus[] }>(
          `/companies/${companyId}/reconciliations${qs}`,
        )
        .then((r) => r.reconciliations);
    },
  });
}

export function useReconciliation(companyId: string | null, id: string) {
  return useQuery({
    queryKey: reconciliationKey(companyId, id),
    enabled: companyId !== null && !!id,
    queryFn: () =>
      api
        .get<{ reconciliation: ReconciliationWithStatus }>(
          `/companies/${companyId}/reconciliations/${id}`,
        )
        .then((r) => r.reconciliation),
  });
}

export function useReconcilableItems(companyId: string | null, id: string) {
  return useQuery({
    queryKey: reconcilableItemsKey(companyId, id),
    enabled: companyId !== null && !!id,
    queryFn: () =>
      api
        .get<{ items: ReconcilableItem[] }>(
          `/companies/${companyId}/reconciliations/${id}/items`,
        )
        .then((r) => r.items),
  });
}

export function useVerifyChain(companyId: string | null, bankAccountId?: string) {
  return useQuery({
    queryKey: ['verify-chain', companyId ?? 'none', bankAccountId ?? 'none'],
    enabled: companyId !== null && !!bankAccountId,
    queryFn: () =>
      api.get<HashChainVerifyResult>(
        `/companies/${companyId}/reconciliations/verify-chain?bankAccountId=${bankAccountId}`,
      ),
  });
}

export function useReconciliationWorksheet(
  companyId: string | null,
  bankAccountId: string | undefined,
) {
  return useQuery({
    queryKey: ['reconciliation-worksheet', companyId ?? 'none', bankAccountId ?? 'none'],
    enabled: companyId !== null && !!bankAccountId,
    queryFn: () =>
      api
        .get<{ worksheet: ReconciliationWorksheet }>(
          `/companies/${companyId}/bank-accounts/${bankAccountId}/reconciliation-worksheet`,
        )
        .then((r) => r.worksheet),
  });
}

// ── Mutations ───────────────────────────────────────────────────────────────

export function useCreateReconciliation(companyId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateReconciliationInput) => {
      if (!companyId) throw new Error('No active company');
      return api
        .post<{ reconciliation: Reconciliation }>(
          `/companies/${companyId}/reconciliations`,
          input,
        )
        .then((r) => r.reconciliation);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['reconciliations', companyId ?? 'none'] });
    },
  });
}

export function useUpdateReconciliation(companyId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      input,
    }: {
      id: string;
      input: UpdateReconciliationInput;
    }) => {
      if (!companyId) throw new Error('No active company');
      return api
        .patch<{ reconciliation: ReconciliationWithStatus }>(
          `/companies/${companyId}/reconciliations/${id}`,
          input,
        )
        .then((r) => r.reconciliation);
    },
    onSuccess: (_data, { id }) => {
      qc.invalidateQueries({ queryKey: reconciliationKey(companyId, id) });
      qc.invalidateQueries({ queryKey: ['reconciliations', companyId ?? 'none'] });
    },
  });
}

export function useAiClearFromStatement(companyId: string | null) {
  return useMutation({
    mutationFn: async ({ id, statementId }: { id: string; statementId: string }) => {
      if (!companyId) throw new Error('No active company');
      return api.post<import('@accounting/shared').AiClearResult>(
        `/companies/${companyId}/reconciliations/${id}/ai-clear`,
        { statementId },
      );
    },
  });
}

/**
 * Attach a statement to an existing reconciliation in ONE step: stored, filed
 * under the month it covers, and linked so the packet carries it.
 *
 * Replaces the detour of uploading to Documents and then relating the two by
 * hand on a second screen.
 */
export function useAttachStatement(companyId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, file }: { id: string; file: File }) => {
      if (!companyId) throw new Error('No active company');
      const content = await fileToBase64(file);
      return api.post<{ statementId: string; fileName: string }>(
        `/companies/${companyId}/reconciliations/${id}/statement`,
        { fileName: file.name, mimeType: file.type || 'application/pdf', content },
      );
    },
    onSuccess: (_r, vars) => {
      qc.invalidateQueries({ queryKey: ['statements', companyId ?? 'none'] });
      qc.invalidateQueries({ queryKey: ['vault', companyId ?? 'none'] });
      qc.invalidateQueries({ queryKey: ['reconciliation', companyId ?? 'none', vars.id] });
    },
  });
}

/** Read a File as base64 without the `data:` URL prefix. */
function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      const comma = result.indexOf(',');
      resolve(comma >= 0 ? result.slice(comma + 1) : result);
    };
    reader.onerror = () => reject(reader.error ?? new Error('Could not read the file'));
    reader.readAsDataURL(file);
  });
}

// ── Starting from the statement document ────────────────────────────────────

/**
 * Read a dropped statement PDF. Creates nothing — everything it returns is a
 * proposal the owner confirms on the form before a reconciliation exists.
 */
export function useParseStatement(companyId: string | null) {
  return useMutation({
    mutationFn: (input: ParseStatementInput) => {
      if (!companyId) throw new Error('No active company');
      return api
        .post<{ parsed: ParsedStatement }>(
          `/companies/${companyId}/reconciliations/parse-statement`,
          input,
        )
        .then((r) => r.parsed);
    },
  });
}

/** Match a confirmed statement against the books. Still creates nothing. */
export function useStatementPreview(companyId: string | null) {
  return useMutation({
    mutationFn: (input: StatementPreviewInput) => {
      if (!companyId) throw new Error('No active company');
      return api
        .post<{ preview: StatementReconPreview }>(
          `/companies/${companyId}/reconciliations/statement-preview`,
          input,
        )
        .then((r) => r.preview);
    },
  });
}

/** Open the reconciliation with the confirmed matches already ticked. */
export function useStartReconFromStatement(companyId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: StartReconFromStatementInput) => {
      if (!companyId) throw new Error('No active company');
      return api
        .post<{ reconciliation: Reconciliation }>(
          `/companies/${companyId}/reconciliations/from-statement`,
          input,
        )
        .then((r) => r.reconciliation);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['reconciliations', companyId ?? 'none'] });
    },
  });
}

/**
 * Brokerage statement: the month's components → the entries they call for,
 * each with its offset account. Creates nothing.
 */
export function useBrokerageProposal(companyId: string | null) {
  return useMutation({
    mutationFn: (input: BrokerageProposalInput) => {
      if (!companyId) throw new Error('No active company');
      return api
        .post<{ proposal: BrokerageProposal }>(
          `/companies/${companyId}/reconciliations/brokerage-proposal`,
          input,
        )
        .then((r) => r.proposal);
    },
  });
}

/** Post the confirmed brokerage components, one entry each, dated the statement date. */
export function usePostBrokerageStatement(companyId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: BrokeragePostInput) => {
      if (!companyId) throw new Error('No active company');
      return api.post<BrokeragePostResult>(
        `/companies/${companyId}/reconciliations/brokerage-post`,
        input,
      );
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['journal-entries', companyId ?? 'none'] });
      qc.invalidateQueries({ queryKey: ['accounts', companyId ?? 'none'] });
      qc.invalidateQueries({ queryKey: ['report'] });
    },
  });
}

export function useDeleteInProgressReconciliation(companyId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => {
      if (!companyId) throw new Error('No active company');
      return api
        .delete<{ deleted: { id: string; statementDate: string } }>(
          `/companies/${companyId}/reconciliations/${id}`,
        )
        .then((r) => r.deleted);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['reconciliations', companyId ?? 'none'] });
      qc.invalidateQueries({ queryKey: ['reconciliation', companyId ?? 'none'] });
      qc.invalidateQueries({ queryKey: ['statements', companyId ?? 'none'] });
    },
  });
}

export function useSetReconciliationItems(companyId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      input,
    }: {
      id: string;
      input: SetReconciliationItemsInput;
    }) => {
      if (!companyId) throw new Error('No active company');
      return api
        .put<{ items: ReconcilableItem[] }>(
          `/companies/${companyId}/reconciliations/${id}/items`,
          input,
        )
        .then((r) => r.items);
    },
    onSuccess: (_data, { id }) => {
      qc.invalidateQueries({ queryKey: reconcilableItemsKey(companyId, id) });
      qc.invalidateQueries({ queryKey: reconciliationKey(companyId, id) });
    },
  });
}

export function useCompleteReconciliation(companyId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      input,
    }: {
      id: string;
      input: CompleteReconciliationInput;
    }) => {
      if (!companyId) throw new Error('No active company');
      return api.post<{ reconciliation: Reconciliation }>(
        `/companies/${companyId}/reconciliations/${id}/complete`,
        input,
      );
    },
    onSuccess: (_data, { id }) => {
      qc.invalidateQueries({ queryKey: ['reconciliations', companyId ?? 'none'] });
      qc.invalidateQueries({ queryKey: reconciliationKey(companyId, id) });
      qc.invalidateQueries({ queryKey: reconcilableItemsKey(companyId, id) });
      qc.invalidateQueries({ queryKey: ['bank-transactions', companyId ?? 'none'] });
      qc.invalidateQueries({ queryKey: ['journal-entries', companyId ?? 'none'] });
      // The BCM month-end packet gate reads completed reconciliations; a stale
      // readiness table would still call this account unreconciled.
      qc.invalidateQueries({ queryKey: ['eom-financials'] });
    },
  });
}

export const linkFiledStatementsPreviewKey = (companyId: string | null) =>
  ['reconciliations', companyId ?? 'none', 'link-filed-statements-preview'] as const;

/**
 * What "link filed statements" WOULD do: the pairings and the reconciliations
 * it cannot resolve. A dry run on the server; nothing is written.
 */
export function useLinkFiledStatementsPreview(companyId: string | null) {
  return useQuery({
    queryKey: linkFiledStatementsPreviewKey(companyId),
    enabled: companyId !== null,
    staleTime: 0,
    queryFn: () =>
      api.post<LinkFiledStatementsResult>(
        `/companies/${companyId}/reconciliations/link-statements`,
        { dryRun: true } satisfies LinkFiledStatementsInput,
      ),
  });
}

/**
 * Pair statements already in the vault with the completed reconciliations they
 * evidence, and re-file those packets.
 */
export function useLinkFiledStatements(companyId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => {
      if (!companyId) throw new Error('No active company');
      return api.post<LinkFiledStatementsResult>(
        `/companies/${companyId}/reconciliations/link-statements`,
        { dryRun: false } satisfies LinkFiledStatementsInput,
      );
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['reconciliations', companyId ?? 'none'] });
      qc.invalidateQueries({ queryKey: ['reconciliation', companyId ?? 'none'] });
      qc.invalidateQueries({ queryKey: ['statements', companyId ?? 'none'] });
      qc.invalidateQueries({ queryKey: ['vault', companyId ?? 'none'] });
      // The month-end packet gate counts statements on file.
      qc.invalidateQueries({ queryKey: ['eom-financials'] });
    },
  });
}

export function useVoidReconciliation(companyId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) => {
      if (!companyId) throw new Error('No active company');
      return api.post<{ voidRecord: Reconciliation }>(
        `/companies/${companyId}/reconciliations/${id}/void`,
        { reason },
      );
    },
    onSuccess: (_data, { id }) => {
      qc.invalidateQueries({ queryKey: ['reconciliations', companyId ?? 'none'] });
      qc.invalidateQueries({ queryKey: reconciliationKey(companyId, id) });
      qc.invalidateQueries({ queryKey: ['bank-transactions', companyId ?? 'none'] });
      qc.invalidateQueries({ queryKey: ['journal-entries', companyId ?? 'none'] });
      // The BCM month-end packet gate reads completed reconciliations; a stale
      // readiness table would still call this account unreconciled.
      qc.invalidateQueries({ queryKey: ['eom-financials'] });
    },
  });
}

/** Correct a completed reconciliation: voids the original and creates a new
 *  in-progress recon pre-populated with the corrections + same items checked.
 *  Returns the new recon so the caller can navigate to it. */
export function useCorrectReconciliation(companyId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      input,
    }: {
      id: string;
      input: CorrectReconciliationInput;
    }) => {
      if (!companyId) throw new Error('No active company');
      return api.post<{
        voidRecord: Reconciliation;
        newReconciliation: Reconciliation;
      }>(`/companies/${companyId}/reconciliations/${id}/correct`, input);
    },
    onSuccess: (_data, { id }) => {
      qc.invalidateQueries({ queryKey: ['reconciliations', companyId ?? 'none'] });
      qc.invalidateQueries({ queryKey: reconciliationKey(companyId, id) });
      qc.invalidateQueries({ queryKey: ['bank-transactions', companyId ?? 'none'] });
      qc.invalidateQueries({ queryKey: ['journal-entries', companyId ?? 'none'] });
      // The BCM month-end packet gate reads completed reconciliations; a stale
      // readiness table would still call this account unreconciled.
      qc.invalidateQueries({ queryKey: ['eom-financials'] });
    },
  });
}
